// frontend/src/components/EnquiryForm/index.tsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
// import { CheckCircle, XCircle, InfoCircle, Send } from "react-bootstrap-icons";
import styles from "./enquiryform.module.css";
import { createEnquiry } from "../../service/enquiry";
import { useAuth } from "../../context/AuthContext";
import { Series } from "../../interface/series";
import { Product } from "../../interface/product";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCircle,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";

interface EnquireModalProps {
  product: Product;
  series: Series;
  show: boolean;
  onHide: () => void;
}

const EnquireModal: React.FC<EnquireModalProps> = ({
  product,
  series,
  show,
  onHide,
}) => {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.emailAddress || "",
    phone: user?.contactNumber || "",
    company: "",
    quantity: "",
    message: "",
    hearAboutUs: "",
    pincode: "",
    address: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Pre-fill user data when modal opens or user changes
  useEffect(() => {
    if (show && user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || prev.name,
        email: user.emailAddress || prev.email,
        phone: user.contactNumber || prev.phone,
      }));
    }
  }, [show, user]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push("Name is required");
    if (!formData.email.trim()) errors.push("Email is required");
    if (!formData.phone.trim()) errors.push("Phone is required");
    if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.push("Valid email is required");
    if (!formData.quantity) errors.push("Please select quantity range");

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const enquiryItems = [
        {
          productId: product._id,
          seriesId: series?._id,
          color: product.color,
          price: product.price,
          ourPrice: product.ourPrice,
          quantity: formData?.quantity,
          productName: product.name,
          seriesTitle: series?.title,
        },
      ];

      const enquiryData = {
        fullName: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        message: formData.message.trim(),
        source: formData.hearAboutUs || "website_form",
        pincode: formData.pincode.trim(),
        address: formData.address.trim(),
        enquiryItems: enquiryItems,
      };

      await createEnquiry(enquiryData);
      setSubmitSuccess(true);
    } catch (err: any) {
      console.error("Submission error:", err);
      setError(
        err.response?.data?.message ||
          "Failed to submit enquiry. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: user?.name || "",
      email: user?.emailAddress || "",
      phone: user?.contactNumber || "",
      company: "",
      quantity: "",
      message: "",
      hearAboutUs: "",
      pincode: "",
      address: "",
    });
    setSubmitSuccess(false);
    setError("");
    setTouched({});
  };

  const handleClose = () => {
    onHide();
    setTimeout(resetForm, 300); // Reset after modal closes
  };

  const getProductPrice = () => {
    return product.ourPrice || product.price;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      className={styles.enquireModal}
      animation
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <div className={styles.titleSection}>
          <div>
            <Modal.Title className={styles.modalTitle}>
              Enquire About Product
            </Modal.Title>
          </div>
          <Badge bg="primary" className={styles.productBadge}>
            Product ID: {product?.productId}
          </Badge>
        </div>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {submitSuccess ? (
          <div className={styles.successState}>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className={styles.successIcon}
            />
            <h3 className={styles.successTitle}>
              Enquiry Submitted Successfully!
            </h3>
            <p className={styles.successMessage}>
              Thank you for your interest in <strong>{product?.name}</strong>.
              We've received your enquiry for quantity range:{" "}
              <strong>{formData.quantity}</strong>.
            </p>
            <p className={styles.successDetail}>
              Our sales team will contact you within 24 hours at{" "}
              {formData.email} or {formData.phone}.
            </p>
            <div className={styles.successActions}>
              <Button
                variant="primary"
                onClick={handleClose}
                className={styles.successButton}
              >
                Close & Continue
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Product Information */}
            <div className={styles.productInfo}>
              <div className={styles.productHeader}>
                <div className={styles.productDetails}>
                  <div className={styles.productImgTittleWrapper}>
                    <img
                      src={product?.picture[0]?.path}
                      alt="Scadel_product_image"
                      className={styles.productImage}
                    />
                    <h5 className={styles.productName}>{product?.name}</h5>
                  </div>

                  <p className={styles.seriesName}>{series?.title}</p>
                  {product.color && (
                    <Badge bg="outline-primary" className={styles.colorBadge}>
                      Color: {product.color}
                    </Badge>
                  )}
                </div>
                <div className={styles.priceSection}>
                  <span className={styles.priceLabel}>Price</span>
                  <span className={styles.productPrice}>
                    ₹{product.ourPrice?.toLocaleString()}
                  </span>
                  {product.ourPrice && product.ourPrice !== product.price && (
                    <span className={styles.originalPrice}>
                      ₹{product.price?.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="danger" className={styles.errorAlert}>
                <FontAwesomeIcon
                  icon={faXmarkCircle}
                  className={styles.errorIcon}
                />
                {error}
              </Alert>
            )}

            {/* Enquiry Form */}
            <Form onSubmit={handleSubmit} className={styles.enquiryForm}>
              <Row>
                <Col md={6}>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label className={styles.formLabel}>
                      Full Name <span className={styles.required}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={() => handleBlur("name")}
                      className={`${styles.formControl} ${
                        touched.name && !formData.name ? styles.error : ""
                      }`}
                      placeholder="Enter your full name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label className={styles.formLabel}>
                      Company
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={styles.formControl}
                      placeholder="Your company name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label className={styles.formLabel}>
                      Email <span className={styles.required}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur("email")}
                      className={`${styles.formControl} ${
                        touched.email && !formData.email ? styles.error : ""
                      }`}
                      placeholder="your.email@example.com"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label className={styles.formLabel}>
                      Phone <span className={styles.required}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => handleBlur("phone")}
                      className={`${styles.formControl} ${
                        touched.phone && !formData.phone ? styles.error : ""
                      }`}
                      placeholder="+91 9876543210"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label className={styles.formLabel}>
                      PIN Code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      className={styles.formControl}
                      placeholder="Enter PIN code"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label className={styles.formLabel}>
                      Estimated Quantity{" "}
                      <span className={styles.required}>*</span>
                    </Form.Label>
                    <Form.Select
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      onBlur={() => handleBlur("quantity")}
                      className={`${styles.formControl} ${
                        touched.quantity && !formData.quantity
                          ? styles.error
                          : ""
                      }`}
                    >
                      <option value="">Select quantity range</option>
                      <option value="1-10">1-10 units</option>
                      <option value="11-50">11-50 units</option>
                      <option value="51-100">51-100 units</option>
                      <option value="101-500">101-500 units</option>
                      <option value="501-1000">501-1000 units</option>
                      <option value="1000+">1000+ units</option>
                      <option value="custom">Custom bulk order</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className={styles.formGroup}>
                <Form.Label className={styles.formLabel}>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={styles.formControl}
                  placeholder="Enter your complete address for better service"
                />
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Form.Label className={styles.formLabel}>
                  How did you hear about us?
                </Form.Label>
                <Form.Select
                  name="hearAboutUs"
                  value={formData.hearAboutUs}
                  onChange={handleChange}
                  className={styles.formControl}
                >
                  <option value="">Select an option</option>
                  <option value="search">Search Engine</option>
                  <option value="social">Social Media</option>
                  <option value="referral">Referral</option>
                  <option value="trade-show">Trade Show</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Form.Label className={styles.formLabel}>
                  Additional Requirements{" "}
                  <span className={styles.required}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={styles.formControl}
                  placeholder="Specify any custom requirements, preferred materials, colors, timelines, or other details..."
                />
                <Form.Text className={styles.helpText}>
                  Please provide as much detail as possible for accurate
                  quotation
                </Form.Text>
              </Form.Group>

              <Button
                type="submit"
                variant="primary"
                disabled={
                  isSubmitting ||
                  !formData?.name ||
                  !formData?.email ||
                  !formData?.message ||
                  !formData.quantity
                }
                className={styles.submitButton}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      size="sm"
                      animation="border"
                      className={styles.spinner}
                    />
                    Submitting...
                  </>
                ) : (
                  <>Submit Product Enquiry</>
                )}
              </Button>
            </Form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <p className={styles.privacyText}>
          By submitting this form, you agree to our{" "}
          <a href="/privacy-policy" className={styles.privacyLink}>
            Privacy Policy
          </a>
          . We'll never share your information with third parties.
        </p>
      </Modal.Footer>
    </Modal>
  );
};

export default EnquireModal;
